<template>
  <FormControl
    :title="title"
    :sub-title="subTitle"
    :label="label"
    :disabled="!editable"
    class="w-full"
  >
    <div
      v-if="editor"
      :dir="locale != 'he' ? 'ltr' : ''"
      class="preview-wrapper group-hover"
      :class="{
        'border border-secondary-250 p-2': editable,
        '!p-0': menuPosition === 'hover',
      }"
      tabindex="0"
    >
      <TiptapMenu
        v-if="editable && editor && menuPosition === 'default'"
        :id="blockId"
        :review="review"
      />
      <div class="flex justify-between items-center">
        <TiptapContent :editor="editor" />
        <div
          class="hover-menu scale-[0.9] transition-opacity duration-300"
          :class="{ 'opacity-100': isFocused, 'opacity-0': !isFocused }"
          v-if="editable && editor && menuPosition === 'hover'"
        >
          <TiptapMenu :id="blockId" :review="review" />
        </div>
      </div>
    </div>
    <BubbleMenu
      v-if="editable && editor && menuPosition === 'bubble'"
      :editor="editor"
      :zoom="zoom"
    >
      <TiptapMenu :id="blockId" :review="review" />
    </BubbleMenu>
    <p
      v-if="$slots.helper"
      class="mt-2 text-sm text-secondary-500 dark:text-secondary-400"
    >
      <slot name="helper" />
    </p>
  </FormControl>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, provide, ref, watch } from 'vue';
import type { PropType } from 'vue';
import FormControl from '@/components/utils/FormControl/FormControl.vue';
import TiptapMenu from './TiptapMenu.vue';
import TiptapContent from './TiptapContent.vue';
import BubbleMenu from './BubbleMenu.vue';
import { useEditor } from './composables/useEditor';
import isEmpty from 'lodash/isEmpty';
import { setExtensionEnabled } from './composables/editorsMap';
import { KeyboardSettings } from '@amit/types';
const { getEditor, createEditor } = useEditor();
const isFocused = ref(false);
const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Array],
    default: '',
  },
  allowEnter: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'my answer',
  },
  subTitle: {
    type: String,
    default: '',
  },
  editable: {
    type: Boolean,
    default: true,
  },
  text: {
    type: [Object, String],
    default: '',
  },
  locale: {
    type: String,
    default: 'he',
  },
  preview: {
    type: Boolean,
    default: true,
  },
  review: {
    type: Boolean,
    default: false,
  },
  zoom: {
    type: Number,
    default: 100,
  },
  menuPosition: {
    type: String as PropType<'default' | 'bubble' | 'hover'>,
    default: 'default',
  },
  menuSize: {
    type: String as PropType<'default' | 'small'>,
    default: 'default',
  },
  keyboardSettings: {
    type: Object as PropType<KeyboardSettings>,
    default: () => ({
      showMathmaticalKeyboard: true,
      keyboardList: [],
    }),
  },
  extensions: {
    type: Object,
    default: () => ({
      line_height: false,
      horizontal_rule: false,
      tooltip: false,
      background_color: false,
      hide_color: false,
      link: false,
      hide_image: false,
      hide_image_resize: false,
      blank_view: false,
    }),
  },
});
const blockId = ref(`${props.preview ? 'preview-' : ''}${props.id}`);
provide('blockId', blockId.value);
provide('keyboardSettings', ref(props.keyboardSettings));

const emit = defineEmits(['update:modelValue', 'keyup.enter', 'blur', 'focus']);
const model = computed(() =>
  !isEmpty(props.modelValue) ? props.modelValue : props.text,
);

const editorExtensions = {
  placeholder: { placeholder: props.placeholder },
  paragraph: {
    HTMLAttributes: {
      textAlign: props.locale === 'he' ? 'right' : 'left',
    },
  },
  hide_katex: props.keyboardSettings.showMathmaticalKeyboard,
  ...props.extensions,
};

createEditor({
  id: blockId.value,
  editable: props.editable && !props.review,
  direction: props.locale != 'he' ? 'ltr' : 'rtl',
  autoFocus: props.editable && !props.review,
  model: model.value,
  onUpdate: () => {
    if (!editor.value) return;
    const html = editor.value?.getHTML();
    emit('update:modelValue', html);
  },
  onEnter: () => {
    emit('keyup.enter');
    console.log('onEnter', props.allowEnter);
    return props.allowEnter;
  },
  onBlur: () => {
    emit('blur');
    isFocused.value = false;
  },
  onFocus: () => {
    emit('focus');
    isFocused.value = true;
  },
  extensions: editorExtensions,
});

const editor = getEditor(blockId.value);

watch(
  () => [editor.value, props.modelValue],
  () => {
    const html = editor.value?.getHTML();
    if (!isEmpty(props.modelValue) && html !== props.modelValue) {
      editor.value?.commands.setContent(props.modelValue);
    }
  },
  { immediate: true },
);

watch(
  () => props.editable,
  value => {
    if (editor.value) {
      editor.value.setOptions({ editable: value && !props.review });

      if (value && !props.review) {
        requestAnimationFrame(() => {
          editor.value?.commands.focus('end');
        });
      }
    }
  },
);

watch(
  () => props.keyboardSettings.showMathmaticalKeyboard,
  value => {
    setExtensionEnabled(props.id, 'katex', value, true);
  },
  { deep: true, immediate: true },
);

defineExpose({
  editor,
});

onUnmounted(() => {
  editor.value?.destroy();
});
</script>
